import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as CalendarIcon } from '@repo/common/assets/icons/20px/icon_20px_calendar_outline.svg';
import { ReactComponent as ClockIcon } from '@repo/common/assets/icons/20px/icon_20px_clock_outline.svg';
import { ReactComponent as PrevIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-left.svg';
import { ReactComponent as NextIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-right.svg';
import { cn } from './model/derive';

/**
 * 날짜·시간 입력 — 브라우저 기본 datetime-local 대신 (달력 아이콘이 안 보이는 브라우저가 있다).
 *   · 글자로 "YYYY-MM-DD HH:mm" 을 이어서 칠 수 있다 (숫자만 쳐도 - · 공백 · : 이 자동으로 들어간다)
 *   · 달력 아이콘 → 달력에서 날짜, 시계 아이콘 → 시간 목록(10분 단위)에서 고른다
 *   · min · max 밖의 날짜·시간은 고를 수 없다
 * 값은 datetime-local 과 같은 'YYYY-MM-DDTHH:mm' (표시 시간대의 벽시계 시각). 다 채워지지 않았거나 틀리면 ''.
 */

const pad = (n) => String(n).padStart(2, '0');
const WEEK = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** 'YYYY-MM-DDTHH:mm' → 'YYYY-MM-DD HH:mm' */
const toText = (v) => (v ? v.replace('T', ' ') : '');

/** 숫자만 뽑아 'YYYY-MM-DD HH:mm' 모양으로 */
const mask = (raw) => {
  const d = raw.replace(/\D/g, '').slice(0, 12);
  let out = d.slice(0, 4);
  if (d.length > 4) out += `-${d.slice(4, 6)}`;
  if (d.length > 6) out += `-${d.slice(6, 8)}`;
  if (d.length > 8) out += ` ${d.slice(8, 10)}`;
  if (d.length > 10) out += `:${d.slice(10, 12)}`;
  return out;
};

/** 'YYYY-MM-DD HH:mm' 가 실제 있는 날짜·시각이면 'YYYY-MM-DDTHH:mm', 아니면 null */
const parse = (text) => {
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(text);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m.map(Number);
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d || h > 23 || mi > 59) return null;
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`;
};

const dayKey = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;

/** 바깥을 누르거나 Escape 면 닫힌다 (입력칸 · 떠 있는 선택창 둘 다 안쪽으로 본다) */
const useDismiss = (open, close, refs) => {
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!refs.some((r) => r.current?.contains(e.target))) close();
    };
    const onKey = (e) => e.key === 'Escape' && close();
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, close]);
};

const POP = { date: { w: 266, h: 316 }, time: { w: 132, h: 250 } };

const Calendar = ({ selected, minDay, maxDay, onPick }) => {
  const start =
    selected || minDay || dayKey(new Date().getFullYear(), new Date().getMonth() + 1, 1);
  const [view, setView] = useState(() => ({
    y: Number(start.slice(0, 4)),
    m: Number(start.slice(5, 7)),
  }));
  const first = new Date(Date.UTC(view.y, view.m - 1, 1)).getUTCDay();
  const days = new Date(Date.UTC(view.y, view.m, 0)).getUTCDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const shift = (n) =>
    setView((v) => {
      const m = v.m + n;
      return m < 1 ? { y: v.y - 1, m: 12 } : m > 12 ? { y: v.y + 1, m: 1 } : { y: v.y, m };
    });
  const firstKey = dayKey(view.y, view.m, 1);
  const lastKey = dayKey(view.y, view.m, days);
  return (
    <div className='w-[264px] p-3'>
      <div className='mb-2 flex items-center justify-between'>
        <button
          type='button'
          aria-label='Previous month'
          disabled={!!minDay && firstKey <= minDay}
          onClick={() => shift(-1)}
          className='flex h-8 w-8 items-center justify-center rounded-lg hover:bg-grayscale-gray01 disabled:opacity-30'
        >
          <PrevIcon className='h-4 w-4' />
        </button>
        <span className='text-[14px] font-medium text-text-primary'>
          {MONTHS[view.m - 1]} {view.y}
        </span>
        <button
          type='button'
          aria-label='Next month'
          disabled={!!maxDay && lastKey >= maxDay}
          onClick={() => shift(1)}
          className='flex h-8 w-8 items-center justify-center rounded-lg hover:bg-grayscale-gray01 disabled:opacity-30'
        >
          <NextIcon className='h-4 w-4' />
        </button>
      </div>
      <div className='grid grid-cols-7 gap-0.5 text-center text-[12px] text-text-secondary'>
        {WEEK.map((w, i) => (
          <span key={`${w}${i}`} className='py-1'>
            {w}
          </span>
        ))}
        {cells.map((d, i) => {
          if (!d) return <span key={`e${i}`} />;
          const key = dayKey(view.y, view.m, d);
          const off = (minDay && key < minDay) || (maxDay && key > maxDay);
          const on = key === selected;
          return (
            <button
              key={key}
              type='button'
              disabled={off}
              onClick={() => onPick(key)}
              className={cn(
                'h-8 rounded-lg text-[13px] transition-colors',
                on
                  ? 'bg-green-deep02 font-medium text-grayscale-gray00'
                  : 'text-text-primary hover:bg-surface-green10',
                off && 'cursor-default text-text-disable hover:bg-transparent',
              )}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DateTimeField = ({ id, value, onChange, min, max, invalid, autoFocus, step = 10 }) => {
  const [text, setText] = useState(toText(value));
  const [open, setOpen] = useState(null); // 'date' | 'time'
  const wrap = useRef(null);
  const pop = useRef(null);
  const timeList = useRef(null);
  const [pos, setPos] = useState(null);
  useDismiss(!!open, () => setOpen(null), [wrap, pop]);

  // 선택창은 모달의 스크롤 영역에 잘리지 않게 화면 위에 띄운다 — 아래 자리가 모자라면 위로 연다
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = wrap.current?.getBoundingClientRect();
      if (!r) return;
      const { w, h } = POP[open];
      const below = window.innerHeight - r.bottom;
      const top = below < h + 8 && r.top > below ? Math.max(8, r.top - h - 4) : r.bottom + 4;
      const left = Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8));
      setPos({ top, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  // 바깥에서 값이 바뀌면 (초기화 등) 글자도 맞춘다
  useEffect(() => {
    if (value && toText(value) !== text) setText(toText(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const minDay = min?.slice(0, 10);
  const maxDay = max?.slice(0, 10);
  const dayPart = /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : '';
  const timePart = /\d{2}:\d{2}$/.test(text) ? text.slice(-5) : '';
  const complete = text.length === 16;
  const badFormat = complete && !parse(text);

  const set = (next) => {
    setText(next);
    onChange(parse(next) ?? '');
  };

  // 고른 날짜의 시간 목록 — min · max 안쪽만
  const times = useMemo(() => {
    const day = dayPart || minDay;
    const out = [];
    for (let m = 0; m < 24 * 60; m += step) {
      const t = `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
      const v = `${day}T${t}`;
      if (min && v < min) continue;
      if (max && v > max) continue;
      out.push(t);
    }
    return out;
  }, [dayPart, minDay, min, max, step]);

  // 시간 목록을 열면 고른 시간(없으면 처음)으로 스크롤
  useEffect(() => {
    if (open !== 'time') return;
    const el = timeList.current?.querySelector('[aria-selected="true"]');
    el?.scrollIntoView({ block: 'center' });
  }, [open]);

  const iconBtn =
    'flex h-8 w-8 items-center justify-center rounded-md text-icon-secondary hover:bg-grayscale-gray01 hover:text-text-primary';

  return (
    <div ref={wrap} className='relative'>
      <div
        className={cn(
          'flex h-10 items-center rounded-lg border bg-grayscale-gray00 pl-3 pr-1 focus-within:border-green-deep02',
          invalid || badFormat ? 'border-functional-failed03' : 'border-green-deep03/20',
        )}
      >
        <input
          id={id}
          value={text}
          autoFocus={autoFocus}
          inputMode='numeric'
          autoComplete='off'
          placeholder='YYYY-MM-DD HH:mm'
          aria-invalid={invalid || badFormat || undefined}
          onChange={(e) => set(mask(e.target.value))}
          className='h-full min-w-0 flex-1 bg-transparent text-[14px] tabular-nums text-text-primary placeholder:text-text-placeholder focus:outline-none'
        />
        <button
          type='button'
          aria-label='Pick a date'
          aria-expanded={open === 'date'}
          onClick={() => setOpen((o) => (o === 'date' ? null : 'date'))}
          className={iconBtn}
        >
          <CalendarIcon className='h-5 w-5' />
        </button>
        <button
          type='button'
          aria-label='Pick a time'
          aria-expanded={open === 'time'}
          onClick={() => setOpen((o) => (o === 'time' ? null : 'time'))}
          className={iconBtn}
        >
          <ClockIcon className='h-5 w-5' />
        </button>
      </div>
      {badFormat && (
        <p role='alert' className='mt-1 text-[12px] text-functional-failed03'>
          Use a real date and time, like 2026-10-16 15:30.
        </p>
      )}

      {open === 'date' &&
        pos &&
        ReactDOM.createPortal(
          <div
            ref={pop}
            style={pos}
            className='fixed z-[1300] rounded-xl border border-green-deep03/[0.08] bg-grayscale-gray00 shadow-popup-01'
          >
            <Calendar
              selected={dayPart}
              minDay={minDay}
              maxDay={maxDay}
              onPick={(day) => {
                // 날짜만 바꾸고 시간은 그대로 (없으면 그날 고를 수 있는 첫 시간)
                const first =
                  [...Array((24 * 60) / step).keys()]
                    .map((i) => `${pad(Math.floor((i * step) / 60))}:${pad((i * step) % 60)}`)
                    .find(
                      (t) => (!min || `${day}T${t}` >= min) && (!max || `${day}T${t}` <= max),
                    ) ?? '00:00';
                const keep =
                  timePart &&
                  (!min || `${day}T${timePart}` >= min) &&
                  (!max || `${day}T${timePart}` <= max);
                set(`${day} ${keep ? timePart : first}`);
                setOpen('time');
              }}
            />
          </div>,
          document.body,
        )}

      {open === 'time' &&
        pos &&
        ReactDOM.createPortal(
          <ul
            ref={(el) => {
              timeList.current = el;
              pop.current = el;
            }}
            role='listbox'
            aria-label='Times'
            style={pos}
            className='fixed z-[1300] max-h-[240px] w-[132px] overflow-y-auto rounded-xl border border-green-deep03/[0.08] bg-grayscale-gray00 p-1 shadow-popup-01'
          >
            {times.length === 0 ? (
              <li className='px-3 py-2 text-[13px] text-text-secondary'>No times on this day</li>
            ) : (
              times.map((t) => (
                <li key={t}>
                  <button
                    type='button'
                    role='option'
                    aria-selected={t === timePart}
                    onClick={() => {
                      set(`${dayPart || minDay} ${t}`);
                      setOpen(null);
                    }}
                    className={cn(
                      'flex h-9 w-full items-center rounded-lg px-3 text-[14px] tabular-nums',
                      t === timePart
                        ? 'bg-surface-green10 font-medium text-text-primary'
                        : 'text-text-primary hover:bg-grayscale-gray01',
                    )}
                  >
                    {t}
                  </button>
                </li>
              ))
            )}
          </ul>,
          document.body,
        )}
    </div>
  );
};

export default DateTimeField;
